import { downloadRemoteImage } from '@modules/image-helpers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useNoRenderRef from './use-no-render-ref';

/** Downloads an image from firebase to a browser blob
 * Requires `image.crossOrigin = "Anonymous";` on the image element which this blob is used in, in order to work.
 */
export default function useRemoteImageBlob(imageUrl: string | undefined | null): {
  blob: Blob | undefined;
  imageUrl: string | undefined;

  updateImageUrl: React.Dispatch<React.SetStateAction<string | undefined>>;
  resetImageUrl: () => void;
  error: boolean;
} {
  const [imageBlobReference, setImageBlobReference] = useState<string>();
  const [imageBlob, setImageBlob] = useState<Blob>();
  const imageBlobRef = useRef<string>(undefined);
  const [isError, setIsError] = useState(false);
  const imageUrlRef = useNoRenderRef(imageUrl);

  useEffect(() => {
    async function handleImage(imageUrl: string) {
      try {
        const imageBlob = await downloadRemoteImage(imageUrl);
        if (imageUrlRef.current !== imageUrl) return;
        // handle case when blobs is be undefined.
        if (!imageBlob) {
          setIsError(true);
          return;
        }

        const url = imageBlob && URL.createObjectURL(imageBlob);

        setImageBlob(imageBlob);
        setImageBlobReference(url);

        imageBlobRef.current = url;
      } catch (err) {
        setIsError(true);
      }
    }

    if (imageUrl) {
      handleImage(imageUrl);
    } else {
      setImageBlob(undefined);
      setImageBlobReference(undefined);
      imageBlobRef.current = undefined;
    }
  }, [imageUrl, imageUrlRef]);

  const resetImageBlobReference = useCallback(() => {
    setImageBlobReference(imageBlobRef.current);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  const result = useMemo(
    () => ({
      blob: imageBlob,
      imageUrl: imageBlobReference,
      updateImageUrl: setImageBlobReference,
      resetImageUrl: resetImageBlobReference,
      error: isError,
    }),
    [imageBlob, imageBlobReference, setImageBlobReference, resetImageBlobReference, isError],
  );

  return result;
}
